import { useCallback } from "react";
import { BinIcon } from "./icons/BinIcon";

interface Props {
  value: number;
  onChange: (next: number) => void;
  min?: number;
  max?: number;
  size?: "sm" | "lg";
  disabled?: boolean;
  /** Affiche un "−" qui passe à 🗑 quand value=1 et qu'un décrément supprimerait la ligne. */
  showRemoveAtOne?: boolean;
}

const sizes = {
  sm: {
    btn: "h-8 w-8 text-base",
    val: "w-8 text-sm",
    container: "rounded-button",
  },
  lg: {
    btn: "h-12 w-12 text-xl",
    val: "w-14 text-lg",
    container: "rounded-button",
  },
};

export const QtyStepper = ({
  value,
  onChange,
  min = 0,
  max = 99,
  size = "sm",
  disabled = false,
  showRemoveAtOne = false,
}: Props): JSX.Element => {
  const s = sizes[size];

  const dec = useCallback(() => {
    if (disabled) return;
    if (value <= min) return;
    onChange(value - 1);
  }, [value, min, disabled, onChange]);

  const inc = useCallback(() => {
    if (disabled) return;
    if (value >= max) return;
    onChange(value + 1);
  }, [value, max, disabled, onChange]);

  const decAtOne = showRemoveAtOne && value === 1;

  return (
    <div
      className={`inline-flex items-center bg-bg border border-border ${s.container} no-select`}
      aria-label="Quantité"
    >
      <button
        type="button"
        onClick={dec}
        disabled={disabled || value <= min}
        className={`${s.btn} flex items-center justify-center text-ink disabled:opacity-30 transition-colors hover:text-primary`}
        aria-label={decAtOne ? "Retirer" : "Diminuer"}
      >
        {decAtOne ? <BinIcon size={size === "lg" ? 20 : 16} /> : "−"}
      </button>
      <span className={`${s.val} text-center font-semibold tabular-nums text-ink`} aria-live="polite">
        {value}
      </span>
      <button
        type="button"
        onClick={inc}
        disabled={disabled || value >= max}
        className={`${s.btn} flex items-center justify-center text-ink disabled:opacity-30 transition-colors hover:text-primary`}
        aria-label="Augmenter"
      >
        +
      </button>
    </div>
  );
};
